import * as React from 'react';

import TagFilter from 'containers/molecules/TagFilter/TagFilter';
import i18n from 'helper/i18n';

import { ALL_FILTERS } from '../factories';
import { FILTER_ID as RECIPE_ALLERGIES_FILTER_ID } from '../factories/recipe/allergies';
import { FILTER_ID as RECIPE_CALORIES_FILTER_ID } from '../factories/recipe/calories';
import { FILTER_ID as RECIPE_DIET_FORM_FILTER_ID } from '../factories/recipe/dietForm';
import { FILTER_ID as RECIPE_DURATION_FILTER_ID } from '../factories/recipe/duration';
import { FILTER_ID as RECIPE_MENU_TYPE_FORM_FILTER_ID } from '../factories/recipe/menuType';
import { FILTER_ID as RECIPE_TOGO } from '../factories/recipe/togo';
import { FILTER_ID as WORKOUT_DURATION_FILTER_ID } from '../factories/workout/duration';
import { FILTER_ID as WORKOUT_INTENSITY_FILTER_ID } from '../factories/workout/intensity';
import { FILTER_ID as WORKOUT_TRAINING_GOAL_FILTER_ID } from '../factories/workout/trainingGoal';

import { PoolEnumType } from 'types/services/magnolia/enums/PoolEnum';
import { ItemTagType, TagFilterItemType, TagFilterItemTypesType } from '../TagFilter';

export type TagFilterPoolOwnPropsType = {
	description: string;
	selectedPool: PoolEnumType;
	selectedCluster: string; // PoolClusterType?
};

export type TagFilterPoolContainerPropsType = {
	filters: {
		[filterId: string]: any;
	};
};

type TagFilterPoolPropsType = TagFilterPoolContainerPropsType & TagFilterPoolOwnPropsType;

export const TagFilterPool = ({ description, selectedPool, selectedCluster, filters = {} }: TagFilterPoolPropsType) => {
	if (selectedPool === 'workout') {
		return (
			<WorkoutPoolTagFilter
				description={description}
				selectedCluster={selectedCluster}
				selectedPool={selectedPool}
				filters={filters}
			/>
		);
	}

	if (selectedPool === 'recipe') {
		return (
			<RecipePoolTagFilter
				description={description}
				selectedCluster={selectedCluster}
				selectedPool={selectedPool}
				filters={filters}
			/>
		);
	}

	return null;
};

const RecipePoolTagFilter = ({ description, selectedPool, selectedCluster, filters = {} }: TagFilterPoolPropsType) => {
	const items: TagFilterItemType[] = [
		{
			filterId: RECIPE_DURATION_FILTER_ID,
			value: filters[RECIPE_DURATION_FILTER_ID],
			type: TagFilterItemTypesType.OVERLAY,
			tag: ItemTagType.RADIO,
			labels: {
				inactive: i18n(
					`react.impulsCoach.components.tagFilter.recipe.${RECIPE_DURATION_FILTER_ID}.placeholder`
				),
				active:
					filters[RECIPE_DURATION_FILTER_ID] &&
					filters[RECIPE_DURATION_FILTER_ID].map((filter: string) => {
						return ALL_FILTERS[RECIPE_DURATION_FILTER_ID].labels[filter];
					}).join(', '),
			},
		},
		{
			filterId: RECIPE_MENU_TYPE_FORM_FILTER_ID,
			value: filters[RECIPE_MENU_TYPE_FORM_FILTER_ID],
			type: TagFilterItemTypesType.OVERLAY,
			tag: ItemTagType.CHECKBOX,
			labels: {
				inactive: i18n(
					`react.impulsCoach.components.tagFilter.recipe.${RECIPE_MENU_TYPE_FORM_FILTER_ID}.placeholder`
				),
				active:
					filters[RECIPE_MENU_TYPE_FORM_FILTER_ID] &&
					filters[RECIPE_MENU_TYPE_FORM_FILTER_ID].map((filter: string) => {
						return ALL_FILTERS[RECIPE_MENU_TYPE_FORM_FILTER_ID].labels[filter];
					}).join(', '),
			},
		},
		{
			filterId: RECIPE_ALLERGIES_FILTER_ID,
			value: filters[RECIPE_ALLERGIES_FILTER_ID],
			type: TagFilterItemTypesType.OVERLAY,
			tag: ItemTagType.CHECKBOX,
			labels: {
				inactive: i18n(
					`react.impulsCoach.components.tagFilter.recipe.${RECIPE_ALLERGIES_FILTER_ID}.placeholder`
				),
				active:
					filters[RECIPE_ALLERGIES_FILTER_ID] &&
					filters[RECIPE_ALLERGIES_FILTER_ID].map((filter: string) => {
						return ALL_FILTERS[RECIPE_ALLERGIES_FILTER_ID].labels[filter];
					}).join(', '),
			},
		},
		{
			filterId: RECIPE_DIET_FORM_FILTER_ID,
			value: filters[RECIPE_DIET_FORM_FILTER_ID],
			type: TagFilterItemTypesType.OVERLAY,
			tag: ItemTagType.RADIO,
			labels: {
				inactive: i18n(
					`react.impulsCoach.components.tagFilter.recipe.${RECIPE_DIET_FORM_FILTER_ID}.placeholder`
				),
				active:
					filters[RECIPE_DIET_FORM_FILTER_ID] &&
					filters[RECIPE_DIET_FORM_FILTER_ID].map((filter: string) => {
						return ALL_FILTERS[RECIPE_DIET_FORM_FILTER_ID].labels[filter];
					}).join(', '),
			},
		},
		{
			filterId: RECIPE_CALORIES_FILTER_ID,
			value: filters[RECIPE_CALORIES_FILTER_ID],
			type: TagFilterItemTypesType.OVERLAY,
			tag: ItemTagType.RADIO,
			labels: {
				inactive: i18n(
					`react.impulsCoach.components.tagFilter.recipe.${RECIPE_CALORIES_FILTER_ID}.placeholder`
				),
				active:
					filters[RECIPE_CALORIES_FILTER_ID] &&
					filters[RECIPE_CALORIES_FILTER_ID].map((filter: string) => {
						return ALL_FILTERS[RECIPE_CALORIES_FILTER_ID].labels[filter];
					}).join(', '),
			},
		},
		{
			filterId: RECIPE_TOGO,
			value: filters[RECIPE_TOGO],
			type: TagFilterItemTypesType.TOGGLE,
			tag: ItemTagType.CHECKBOX,
			labels: {
				inactive: i18n(`react.impulsCoach.components.tagFilter.recipe.${RECIPE_TOGO}.placeholder`),
				active:
					filters[RECIPE_TOGO] &&
					filters[RECIPE_TOGO].map((filter: string) => {
						return ALL_FILTERS[RECIPE_TOGO].labels[filter];
					}).join(', '),
			},
		},
	];

	return (
		<TagFilter
			selectedPool={selectedPool}
			selectedCluster={selectedCluster}
			description={description}
			items={items}
		/>
	);
};

const WorkoutPoolTagFilter = ({ description, selectedPool, selectedCluster, filters = {} }: TagFilterPoolPropsType) => {
	const items: TagFilterItemType[] = [
		{
			filterId: WORKOUT_DURATION_FILTER_ID,
			value: filters[WORKOUT_DURATION_FILTER_ID],
			type: TagFilterItemTypesType.OVERLAY,
			tag: ItemTagType.RADIO,
			labels: {
				inactive: i18n(
					`react.impulsCoach.components.tagFilter.workout.${WORKOUT_DURATION_FILTER_ID}.placeholder`
				),
				active: ALL_FILTERS[WORKOUT_DURATION_FILTER_ID].labels[filters[WORKOUT_DURATION_FILTER_ID]],
			},
		},
		{
			filterId: WORKOUT_TRAINING_GOAL_FILTER_ID,
			value: filters[WORKOUT_TRAINING_GOAL_FILTER_ID],
			type: TagFilterItemTypesType.OVERLAY,
			tag: ItemTagType.RADIO,
			labels: {
				inactive: i18n(
					`react.impulsCoach.components.tagFilter.workout.${WORKOUT_TRAINING_GOAL_FILTER_ID}.placeholder`
				),
				active: ALL_FILTERS[WORKOUT_TRAINING_GOAL_FILTER_ID].labels[filters[WORKOUT_TRAINING_GOAL_FILTER_ID]],
			},
		},
		{
			filterId: WORKOUT_INTENSITY_FILTER_ID,
			value: filters[WORKOUT_INTENSITY_FILTER_ID],
			type: TagFilterItemTypesType.OVERLAY,
			tag: ItemTagType.RADIO,
			labels: {
				inactive: i18n(
					`react.impulsCoach.components.tagFilter.workout.${WORKOUT_INTENSITY_FILTER_ID}.placeholder`
				),
				active: ALL_FILTERS[WORKOUT_INTENSITY_FILTER_ID].labels[filters[WORKOUT_INTENSITY_FILTER_ID]],
			},
		},
	];

	return (
		<TagFilter
			selectedPool={selectedPool}
			selectedCluster={selectedCluster}
			description={description}
			items={items}
		/>
	);
};

export default TagFilterPool;
